import { AdditionalInformation } from '@/components/employee-profile/additional-information'
import { CapabilityShowcase } from '@/components/employee-profile/capability-showcase'
import { EmployeeDescription } from '@/components/employee-profile/employee-description'
import { EmployeeHeroHeader } from '@/components/employee-profile/employee-hero-header'
import { ImageGallery } from '@/components/employee-profile/image-gallery'
import { RelatedEmployees } from '@/components/employee-profile/related-employees'
import { ReviewsSection } from '@/components/employee-profile/reviews-section'
import { Button } from '@/components/ui/button'
import { getEmployeeProfile } from '@/data/mock-data'
import { usePageTitle } from '@/hooks/use-page-title'
import type { EmployeeProfile } from '@/types'
import { useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'

/**
 * Main Employee Profile Page Component
 * 
 *  Store layout with:
 * - Clean hero section with product info and actions
 * - Screenshots section with navigation
 * - Sidebar with related content
 * - Organized sections for description, ratings, features
 */
export function EmployeeProfilePage() {
  const { id } = useParams<{ id: string }>()
  const [isScrolled, setIsScrolled] = useState(false)

  const employee = id ? getEmployeeProfile(id) : null
  const pageTitle = usePageTitle(
    employee ? `Employee - ${employee.name}` : "Employee Profile",
    "Discover AI employees with specialized skills and capabilities for your team"
  )

  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.getElementById('hero-section')
      if (heroSection) {
        const heroBottom = heroSection.offsetTop + heroSection.offsetHeight
        setIsScrolled(window.scrollY > heroBottom - 100)
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleAddToWorkspace = (employee: EmployeeProfile) => {
    console.log('Adding to workspace:', employee.name)
    alert(`${employee.name} has been added to your workspace!`)
  }

  if (!id) {
    return <Navigate to="/" replace />
  }

  if (!employee) {
    return (
      <>
        {pageTitle}
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">
              Employee Not Found
            </h1>
            <p className="text-muted-foreground mb-6">
              The AI employee you're looking for doesn't exist or has been moved.
            </p>
            <Button>
              <a href="/">
                Back to Homepage
              </a>
            </Button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {pageTitle}
      <div>
      <EmployeeHeroHeader 
        employee={employee}
        isScrolled={isScrolled}
        onAddToWorkspace={handleAddToWorkspace}
      />

      {/* Main Content Area */}
      <div className='bg-main-content'>
        <div className="container mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Left Column - Screenshots and Content */}
            <div className="lg:col-span-3 space-y-6">
              {/* Screenshots Section */}
              <div>
                <h2 className="text-lg font-semibold mb-3">Screenshots</h2>
                <ImageGallery employee={employee} />
              </div>

              {/* Description */}
              <div>
                <h2 className="text-lg font-semibold mb-3">Description</h2>
                <EmployeeDescription employee={employee} />
              </div>

              {/* Ratings and Reviews */}
              <ReviewsSection employee={employee} />

              {/* Features */}
              <CapabilityShowcase employee={employee} />


              {/* Additional Information */}
              <AdditionalInformation employee={employee} />
            </div>

            {/* Right Sidebar - Discover More */}
            <div className="sticky top-20 space-y-6 h-fit">
              <div>
                <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
                  Discover more
                  <span className="text-blue-600">›</span>
                </h3>
                <RelatedEmployees employee={employee} />
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  )
}


EmployeeProfilePage.displayName = "EmployeeProfilePage"